import { useContext, useEffect, useState } from "react";
import { Button, Input, Select, Table, Tags } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { formatSar, hasValidSarPrecision, sarToMinorUnits } from "../../../../utils/billingMoney";
import {
  createServiceCatalogItemAPI,
  listServiceCatalogAdminAPI,
  updateServiceCatalogItemAPI,
} from "../../../../utils/api/billing";
import { useBillingAdminText } from "../billingAdminI18n";

const BILLING_MODES = ["fixed", "per_unit", "per_branch", "per_app", "monthly"];

const emptyForm = { code: "", nameAr: "", nameEn: "", descriptionAr: "", price: "", billingMode: "fixed", unitLabelAr: "", customerOrderable: true, active: true };

// Backend-provided sarToMinorUnits is validation-strict (throws on bad
// input); this local variant just parses for display/editing purposes and
// leaves real validation to hasValidSarPrecision + the server.
function sarStringFromMinor(minor) {
  return (Number(minor || 0) / 100).toFixed(2);
}

const ServicesPanel = ({ canManage, onChanged }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listServiceCatalogAdminAPI(token);
      setServices(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (service) => {
    setEditing(service._id);
    setForm({
      code: service.code,
      nameAr: service.nameAr,
      nameEn: service.nameEn,
      descriptionAr: service.descriptionAr || "",
      price: sarStringFromMinor(service.unitPriceMinor),
      billingMode: service.billingMode,
      unitLabelAr: service.unitLabelAr || "",
      customerOrderable: service.customerOrderable,
      active: service.active,
    });
  };

  const startCreate = () => {
    setEditing("new");
    setForm({ ...emptyForm });
  };

  const cancel = () => {
    setEditing(null);
    setForm(null);
    setError("");
  };

  const save = async () => {
    if (!can("billing.admin.settings.manage")) return;
    if (!form.nameAr.trim() || !form.nameEn.trim()) {
      setError(text.settings.serviceModal.namesRequired);
      return;
    }
    if (editing === "new" && !form.code.trim()) {
      setError(text.settings.serviceModal.codeRequired);
      return;
    }
    if (!hasValidSarPrecision(form.price)) {
      setError(text.plans.basePricePrecision);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        nameAr: form.nameAr,
        nameEn: form.nameEn,
        descriptionAr: form.descriptionAr,
        unitPriceMinor: sarToMinorUnits(form.price),
        billingMode: form.billingMode,
        unitLabelAr: form.unitLabelAr,
        customerOrderable: form.customerOrderable,
        active: form.active,
      };
      if (editing === "new") await createServiceCatalogItemAPI(token, { ...payload, code: form.code });
      else await updateServiceCatalogItemAPI(token, editing, payload);
      cancel();
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "name", label: text.settings.serviceModal.name, render: (service) => <div className="billing-plan-name"><strong>{service.nameAr}</strong><span>{service.nameEn}</span></div> },
    { key: "description", label: text.settings.serviceModal.description2, render: (service) => service.descriptionAr || "-" },
    { key: "price", label: text.settings.serviceModal.price, render: (service) => formatSar(service.unitPriceMinor, lang) },
    { key: "billingMode", label: text.settings.serviceModal.billingMode, render: (service) => text.settings.serviceModal.billingModes[service.billingMode] || service.billingMode },
    { key: "unit", label: text.settings.serviceModal.unitLabel, render: (service) => service.unitLabelAr || "-" },
    { key: "customerOrderable", label: text.settings.serviceModal.customerOrderable, render: (service) => <Tags variant={service.customerOrderable ? "success" : "neutral"}>{service.customerOrderable ? text.settings.serviceModal.orderable : text.settings.serviceModal.notOrderable}</Tags> },
    { key: "status", label: text.common.status, render: (service) => <Tags variant={service.active ? "success" : "neutral"}>{service.active ? text.settings.serviceModal.active : text.settings.serviceModal.inactive || "-"}</Tags> },
    {
      key: "actions",
      label: text.common.actions,
      render: (service) =>
        canManage ? (
          <Button size="sm" appearance="outline" onClick={() => startEdit(service)}>
            {text.common.edit}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="billing-settings-panel">
      <p className="billing-settings-panel-note">{text.settings.serviceModal.description}</p>
      {error && <div className="billing-error">{error}</div>}
      {loading ? (
        <div className="billing-loading">{text.common.loading}</div>
      ) : form ? (
        <div className="billing-plan-modal-form">
          {editing === "new" && (
            <Input label={text.plans.code} value={form.code} onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))} required />
          )}
          <div className="billing-form-grid">
            <Input label={text.plans.arabicName} value={form.nameAr} onChange={(event) => setForm((current) => ({ ...current, nameAr: event.target.value }))} required />
            <Input label={text.plans.englishName} value={form.nameEn} onChange={(event) => setForm((current) => ({ ...current, nameEn: event.target.value }))} required />
          </div>
          <Input textarea label={text.settings.serviceModal.description2} value={form.descriptionAr} onChange={(event) => setForm((current) => ({ ...current, descriptionAr: event.target.value }))} />
          <div className="billing-form-grid">
            <Input label={text.settings.serviceModal.price} value={form.price} onChange={(event) => setForm((current) => ({ ...current, price: event.target.value }))} />
            <Select
              label={text.settings.serviceModal.billingMode}
              value={form.billingMode}
              onChange={(event) => setForm((current) => ({ ...current, billingMode: event.target.value }))}
              options={BILLING_MODES.map((mode) => ({ value: mode, label: text.settings.serviceModal.billingModes[mode] }))}
            />
          </div>
          <Input label={text.settings.serviceModal.unitLabel} value={form.unitLabelAr} onChange={(event) => setForm((current) => ({ ...current, unitLabelAr: event.target.value }))} />
          <div className="billing-inline-toggles">
            <label>
              <input type="checkbox" checked={form.customerOrderable} onChange={(event) => setForm((current) => ({ ...current, customerOrderable: event.target.checked }))} />
              {text.settings.serviceModal.customerOrderable}
            </label>
            <label>
              <input type="checkbox" checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} />
              {text.settings.serviceModal.active}
            </label>
          </div>
          <div className="billing-modal-actions">
            <Button appearance="filled" loading={saving} onClick={save}>
              {editing === "new" ? text.settings.serviceModal.create : text.settings.serviceModal.save}
            </Button>
            <Button appearance="ghost" onClick={cancel}>
              {text.common.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Table columns={columns} data={services} loading={loading} emptyText={text.settings.serviceModal.empty} />
          {canManage && (
            <Button appearance="filled" onClick={startCreate}>
              {text.settings.serviceModal.addNew}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default ServicesPanel;
